import React, { useMemo, useRef, useState } from 'react';

import {
  alpha,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select as MuiSelect,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent, SelectProps } from '@mui/material';
import clsx from 'clsx';
import { SmallCloseIconButton } from 'src/components';
import { usePopoverDimensions } from 'src/components/ai/chat/chat-hooks/usePopoverPosition';
import { BackArrowIcon } from 'src/components/icons/BackArrowIcon';
import { MagnifyingGlassIcon } from 'src/components/icons/MagnifyingGlassIcon';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

const DEFAULT_MAX_HEIGHT_PX = 560;
const MARGIN_THRESHOLD_PX = 8;

const useStyles = makeStyles()(theme => ({
  backArrowIcon: {
    fontSize: 18,
    height: 18,
    width: 18,
  },
  groupHeader: {
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    boxSizing: 'border-box',
    color: theme.palette.text.primary,
    display: 'flex',
    fontFamily: theme.typography.fontFamily,
    fontSize: 16,
    fontWeight: 700,
    width: '100%',
  },
  hiddenTrigger: {
    height: 0,
    opacity: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    position: 'absolute',
    width: 0,
  },
  menuTitle: {
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    fontSize: 16,
    fontWeight: 700,
    margin: 0,
  },
  menuTitleRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  searchField: {
    '& .MuiInputAdornment-root': {
      marginRight: 0,
    },
    '& .MuiInputBase-input': {
      padding: 0,
    },
    '& .MuiInputBase-root': {
      backgroundColor: alpha(theme.palette.divider, 0.16),
      borderRadius: 8,
      boxSizing: 'border-box',
      gap: theme.spacing(1),
      padding: theme.spacing(1),
    },
    maxWidth: '100%',
  },
  searchHeader: {
    backgroundColor: theme.appColors.background,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    lineHeight: 'normal',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1, 2),
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  selectRoot: {
    '& .MuiSelect-select': {
      borderRadius: 6,
      cursor: 'pointer',
      paddingBottom: 4,
      paddingLeft: 6,
      paddingTop: 4,
      transition: theme.transitions.create('background-color', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    '& .MuiSelect-select:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-disabled .MuiSelect-select:hover': {
      backgroundColor: 'transparent',
    },
  },
  suggestedGroupHeader: {
    alignItems: 'center',
    backgroundColor: theme.appColors.textHighlight,
    boxSizing: 'border-box',
    color: theme.appColors.primary,
    display: 'flex',
    fontFamily: theme.typography.fontFamily,
    fontSize: 16,
    fontWeight: 700,
    width: '100%',
  },
  suggestedItem: {
    backgroundColor: theme.appColors.textHighlight,
  },
}));

export type RelationshipGroup = {
  header: string;
  items: Apollo.ChildRelationshipType[];
};

export const DEFAULT_RELATIONSHIP_GROUPS: RelationshipGroup[] = [
  {
    header: 'Modification',
    items: [
      Apollo.ChildRelationshipType.Amendment,
      Apollo.ChildRelationshipType.Supersede,
      Apollo.ChildRelationshipType.Correction,
      Apollo.ChildRelationshipType.Supplement,
    ],
  },
  {
    header: 'Lifecycle',
    items: [
      Apollo.ChildRelationshipType.Renewal,
      Apollo.ChildRelationshipType.Extension,
      Apollo.ChildRelationshipType.Termination,
    ],
  },
  {
    header: 'Dependency',
    items: [
      Apollo.ChildRelationshipType.Incorporation,
      Apollo.ChildRelationshipType.Appendix,
      Apollo.ChildRelationshipType.Schedule,
      Apollo.ChildRelationshipType.Exhibit,
      Apollo.ChildRelationshipType.GovernedBy,
    ],
  },
  {
    header: 'Successor / Transfer',
    items: [
      Apollo.ChildRelationshipType.Assignment,
      Apollo.ChildRelationshipType.Novation,
      Apollo.ChildRelationshipType.Flowdown,
    ],
  },
  {
    header: 'Loose Linking',
    items: [Apollo.ChildRelationshipType.Related, Apollo.ChildRelationshipType.Mention],
  },
];

type RelationshipTypeValue = Apollo.ChildRelationshipType | '';

export interface RelationshipTypeSelectProps extends Omit<
  SelectProps<RelationshipTypeValue>,
  'children' | 'onChange' | 'value'
> {
  getLabel: (type: Apollo.ChildRelationshipType) => string;
  onChange: (event: SelectChangeEvent<RelationshipTypeValue>, child: React.ReactNode) => void;
  value: RelationshipTypeValue;
  anchorEl?: HTMLElement | null;
  groups?: RelationshipGroup[];
  menuTitle?: string;
  onBack?: () => void;
  searchPlaceholder?: string;
  suggestedItems?: Apollo.ChildRelationshipType[];
}

export function RelationshipTypeSelect({
  MenuProps,
  anchorEl,
  className,
  getLabel,
  groups = DEFAULT_RELATIONSHIP_GROUPS,
  menuTitle,
  onBack,
  onChange,
  onClose,
  onOpen,
  open: openProp,
  searchPlaceholder = 'Search for a type',
  suggestedItems,
  value,
  ...rest
}: RelationshipTypeSelectProps): JSX.Element {
  const { classes } = useStyles();
  const [search, setSearch] = useState('');
  const [internalOpen, setInternalOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement | null>(null);
  const isControlled = openProp !== undefined;
  const isOpen = openProp ?? internalOpen;
  const {
    maxHeight: dynamicMaxHeight,
    openAbove,
    width: anchorWidth,
  } = usePopoverDimensions(anchorEl ?? selectRef.current, {
    defaultHeightPx: DEFAULT_MAX_HEIGHT_PX,
    marginThresholdPx: MARGIN_THRESHOLD_PX,
  });

  const normalizedSearch = search.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!normalizedSearch) return groups;
    return groups
      .map(group => ({
        ...group,
        items: group.items.filter(type => getLabel(type).toLowerCase().includes(normalizedSearch)),
      }))
      .filter(group => group.items.length > 0);
  }, [getLabel, groups, normalizedSearch]);

  const filteredSuggestedItems = useMemo(() => {
    if (!suggestedItems?.length) return [];
    if (!normalizedSearch) return suggestedItems;
    return suggestedItems.filter(type => getLabel(type).toLowerCase().includes(normalizedSearch));
  }, [getLabel, normalizedSearch, suggestedItems]);

  const hasMatches = filteredGroups.some(g => g.items.length > 0);

  function renderMenuItems(): React.ReactNode {
    if (!hasMatches && !filteredSuggestedItems.length && normalizedSearch) {
      return (
        <Typography align="center" variant="subtitle2">
          No results
        </Typography>
      );
    }

    const suggestedNodes = filteredSuggestedItems.length
      ? [
          <ListSubheader
            key="header-suggested"
            disableSticky
            className={classes.suggestedGroupHeader}
          >
            Suggested
          </ListSubheader>,
          ...filteredSuggestedItems.map(type => (
            <MenuItem
              key={`suggested-${type}`}
              className={classes.suggestedItem}
              sx={theme => ({ color: alpha(theme.palette.text.primary, 0.87) })}
              value={type}
            >
              {getLabel(type)}
            </MenuItem>
          )),
        ]
      : [];

    const groupNodes = filteredGroups.flatMap(group => [
      <ListSubheader key={`header-${group.header}`} disableSticky className={classes.groupHeader}>
        {group.header}
      </ListSubheader>,
      ...group.items.map(type => (
        <MenuItem
          key={type}
          sx={theme => ({ color: alpha(theme.palette.text.primary, 0.87) })}
          value={type}
        >
          {getLabel(type)}
        </MenuItem>
      )),
    ]);

    return [...suggestedNodes, ...groupNodes];
  }

  const closeMenu = (event: React.SyntheticEvent) => {
    setSearch('');
    setInternalOpen(false);
    onClose?.(event);
  };

  const parentSlotProps = MenuProps?.slotProps;
  const parentListProps = typeof parentSlotProps?.list === 'object' ? parentSlotProps.list : {};
  const parentPaperProps = typeof parentSlotProps?.paper === 'object' ? parentSlotProps.paper : {};

  return (
    <MuiSelect<RelationshipTypeValue>
      {...rest}
      ref={selectRef}
      MenuProps={{
        ...MenuProps,
        ...(anchorEl ? { anchorEl } : {}),
        anchorOrigin: {
          horizontal: 'left',
          vertical: openAbove ? 'top' : 'bottom',
        },
        autoFocus: false,
        disableAutoFocus: true,
        disableAutoFocusItem: true,
        onKeyDown: e => e.stopPropagation(),
        slotProps: {
          ...parentSlotProps,
          list: {
            ...parentListProps,
            sx: {
              maxHeight: dynamicMaxHeight,
              overflowY: 'auto',
              pt: 0,
            },
          },
          paper: {
            ...parentPaperProps,
            sx: {
              maxHeight: dynamicMaxHeight,
              minWidth: anchorWidth,
              overflow: 'hidden',
            },
          },
        },
        transformOrigin: {
          horizontal: 'left',
          vertical: openAbove ? 'bottom' : 'top',
        },
      }}
      className={clsx(
        anchorEl ? classes.hiddenTrigger : classes.selectRoot,
        !anchorEl && className
      )}
      native={false}
      open={isOpen}
      sx={
        anchorEl
          ? undefined
          : theme => ({
              '& .MuiSelect-select': {
                backgroundColor: isOpen ? theme.palette.action.selected : undefined,
              },
            })
      }
      value={value}
      onChange={onChange}
      onClose={closeMenu}
      onOpen={event => {
        if (!isControlled) setInternalOpen(true);
        onOpen?.(event);
      }}
    >
      <ListSubheader className={classes.searchHeader}>
        {Boolean(menuTitle) && (
          <div className={classes.menuTitleRow}>
            {onBack && (
              <IconButton
                size="small"
                sx={{ color: 'text.primary', padding: 0 }}
                onMouseDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  closeMenu(e);
                  onBack();
                }}
              >
                <BackArrowIcon className={classes.backArrowIcon} />
              </IconButton>
            )}
            <Typography className={classes.menuTitle}>{menuTitle}</Typography>
          </div>
        )}
        <TextField
          autoFocus
          fullWidth
          autoComplete="off"
          className={classes.searchField}
          placeholder={searchPlaceholder}
          size="small"
          slotProps={{
            input: {
              disableUnderline: true,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <SmallCloseIconButton
                    onMouseDown={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSearch('');
                    }}
                  />
                </InputAdornment>
              ) : undefined,
              startAdornment: (
                <InputAdornment position="start">
                  <MagnifyingGlassIcon height={14} width={14} />
                </InputAdornment>
              ),
            },
          }}
          value={search}
          variant="standard"
          onChange={e => setSearch(e.target.value)}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        />
      </ListSubheader>
      {renderMenuItems()}
    </MuiSelect>
  );
}
